import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { useService } from '../../contexts/ServiceContext';

const POLL_INTERVAL_MS = 2000;
const POLL_CEILING_MS = 60_000;

const WELCOME_TITLE = 'Welcome to listing management';
const WELCOME_SUBTITLE =
  'We are syncing your listings. This is a one time operation.';
const STALL_MESSAGE =
  'This is taking longer than expected. Refresh to try again.';

/**
 * First-login gate shown in place of the Overview while the
 * server-side catchup sync is in flight. Polls the backend every 2s
 * and refreshes /me when the sync transitions to a terminal state.
 *
 * Renders only when the parent has determined the gate is active.
 * The component drops itself by re-fetching the session and letting
 * the parent re-render through the un-gated path.
 */
export function CatchupSync() {
  const { getCatchupSync } = useService();
  const { checkAuthentication } = useAuth();
  const [stalled, setStalled] = useState(false);
  const settledRef = useRef(false);

  const stop = useCallback(() => {
    settledRef.current = true;
  }, []);

  useEffect(() => {
    const startedAt = Date.now();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tick = async () => {
      if (cancelled || settledRef.current) return;

      try {
        const result = await getCatchupSync();
        if (cancelled) return;

        if (result.done) {
          stop();
          await checkAuthentication();
          return;
        }
      } catch (err) {
        // Network / 5xx during the poll — keep trying until the
        // ceiling. We don't want a transient blip to permanently
        // strand the user behind the gate.
        console.warn('CatchupSync: poll failed', err);
      }

      if (cancelled) return;

      if (Date.now() - startedAt >= POLL_CEILING_MS) {
        setStalled(true);
        stop();
        return;
      }

      timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timeoutId = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [getCatchupSync, checkAuthentication, stop]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        py: 10,
        minHeight: 'calc(100vh - 200px)',
        textAlign: 'center',
        px: 3,
      }}
    >
      {!stalled && <CircularProgress size={64} />}
      <Typography variant="h4" sx={{ fontWeight: 500 }}>
        {WELCOME_TITLE}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
        {stalled ? STALL_MESSAGE : WELCOME_SUBTITLE}
      </Typography>
      {stalled && (
        <Button variant="contained" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      )}
    </Box>
  );
}
